import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import "@/phone.css";                                          // Batch 19 · Stage C (C3): the phone layout, < 768 px only
import App from "@/App";
import { reloadOnceForChunkError, isChunkLoadError, markChunkFailedAgain, scheduleRecoveredChunkReport } from "@/lib/chunkReload";   // Batch 10 · B16; STEP 10 · C2
import api from "@/lib/api";
import { registerServiceWorker, captureInstallPrompt } from "@/lib/pwa";   // Batch 19 · Stage C (C1 / C2)

// Global safety net for transient network hiccups (Cloudflare 502 during
// a backend restart, DNS blips, canceled requests, etc.). The axios
// response interceptor at src/lib/api.js already surfaces failures as a
// friendly toast — the reject that propagates from there is only there
// so individual callers can also `.catch` their own follow-up. If no
// caller catches it, CRA's react-error-overlay flashes "Uncaught runtime
// errors" which is scary and misleading (the user already saw the toast
// and there is nothing broken).
//
// CRITICAL: use CAPTURE phase + stopImmediatePropagation so we run BEFORE
// react-error-overlay's own listener (which registers in the bubble
// phase). Cover BOTH `error` and `unhandledrejection` since axios can
// surface transient network failures through either channel depending on
// where the reject chain terminates.
const isAxiosLike = (v) =>
  v &&
  (v.isAxiosError ||
    v?.config?.baseURL != null ||
    typeof v?.toJSON === "function" ||
    /Request failed with status code|Network Error|timeout of \d+ms exceeded/i.test(
      v?.message || "",
    ));

// Batch 10 · B16: a chunk request that fails outside a React error boundary (a dynamic import in a handler) reloads the page once
// as well — the same one-per-minute guard as the boundaries.
window.addEventListener("unhandledrejection", (event) => {
  if (reloadOnceForChunkError(event.reason)) { event.preventDefault(); event.stopImmediatePropagation(); }
  else if (isChunkLoadError(event.reason)) markChunkFailedAgain();   // STEP 10 · C2: the reload did not cure it — not "recovered"
}, true);

window.addEventListener(
  "unhandledrejection",
  (event) => {
    if (isAxiosLike(event.reason)) {
      // eslint-disable-next-line no-console
      console.warn("[api] handled rejection:", event.reason?.message || event.reason);
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  },
  true, // capture phase — runs before react-error-overlay's bubble-phase listener
);

window.addEventListener(
  "error",
  (event) => {
    if (isAxiosLike(event.error) || isAxiosLike(event.reason)) {
      // eslint-disable-next-line no-console
      console.warn("[api] handled error:", event.error?.message || event.message);
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  },
  true,
);

// Dev-only belt-and-braces #1: CRA's legacy react-error-overlay hook.
if (process.env.NODE_ENV === "development") {
  try {
    // eslint-disable-next-line global-require
    const overlay = require("react-error-overlay");
    if (overlay && typeof overlay.stopReportingRuntimeErrors === "function") {
      overlay.stopReportingRuntimeErrors();
    }
  } catch {
    /* not installed — nothing to do */
  }
}

// Dev-only belt-and-braces #2: webpack-dev-server v5 renders its OWN
// overlay via an iframe with id `webpack-dev-server-client-overlay`,
// wired directly to the WDS WebSocket — independent of window `error`
// / `unhandledrejection` events, so preventDefault above does NOT stop
// it. We install a MutationObserver that removes the iframe whenever
// its rendered error text matches a transient network failure (502,
// axios "Request failed", generic Network Error). Real compile errors
// or genuine app crashes still surface normally because their text
// does NOT match this narrow allowlist.
if (typeof MutationObserver !== "undefined") {
  const TRANSIENT_RX =
    /Request failed with status code 5\d{2}|Network Error|502 Bad Gateway|Failed to fetch|timeout of \d+ms exceeded|status code 502/i;
  const TARGET_ID = "webpack-dev-server-client-overlay";
  const dismiss = (iframe) => {
    try {
      const doc = iframe.contentDocument;
      const text = (doc?.body?.innerText || "").slice(0, 4000);
      if (TRANSIENT_RX.test(text)) {
        iframe.remove();
        // eslint-disable-next-line no-console
        console.warn("[wds-overlay] dismissed transient network overlay");
      }
    } catch {
      /* cross-origin iframe (unlikely in dev) — ignore */
    }
  };
  const scan = () => {
    document.querySelectorAll(`iframe#${TARGET_ID}`).forEach((f) => {
      // iframe may still be loading — retry once the doc is ready.
      const tryDismiss = () => dismiss(f);
      tryDismiss();
      f.addEventListener("load", tryDismiss, { once: true });
    });
  };
  const obs = new MutationObserver(scan);
  const start = () => obs.observe(document.body, { childList: true, subtree: true });
  if (document.body) start();
  else document.addEventListener("DOMContentLoaded", start, { once: true });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

// Batch 19 · Stage C: the shell service worker (production builds) and the browser's install prompt (kept for /install)
captureInstallPrompt();
registerServiceWorker();

// STEP 10 · C2: a chunk error cured by the one reload is reported now, as recovered (a notice on the Founder Errors tab)
scheduleRecoveredChunkReport((url, body, opts) => api.post(url, body, opts));

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
);
